import React, { PropsWithChildren, ReactElement } from 'react';
import cx from 'classnames';
import { SxProps, Theme, styled } from '@mui/material/styles';
import { Card, CardActions, CardContent } from '@mui/material';
import { borderRadius, boxShadow } from 'new/constants/styles';

type StyledCardProps = {
  isFullPage?: boolean;
  hasBreadcrumbs?: boolean;
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== 'isFullPage' && prop !== 'hasBreadcrumbs',
})<StyledCardProps>(({ isFullPage, hasBreadcrumbs, theme }) => {
  const defaultStyles = {
    height: `calc(100vh - ${hasBreadcrumbs ? '125' : '160'}px)`,
    width: `calc(100% - ${theme.spacing(12)})`,
    maxWidth: 1620,
    marginTop: 40,
    borderRadius,
    boxShadow,
  };
  const fullPageStyles = {
    height: '100vh',
    borderRadius: 0,
    boxShadow: 'none',
  };
  const layoutSizeStyles = isFullPage ? fullPageStyles : defaultStyles;

  return {
    margin: 'auto',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    ...layoutSizeStyles,

    '.cardContent': {
      flexGrow: '1',
      overflowX: 'auto',
      marginTop: theme.spacing(3),
    },

    '.cardFooter': {
      height: '6.25rem',
      flexShrink: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: theme.spacing(4),
      marginBottom: isFullPage ? theme.spacing(16) : 0,
    },
  };
});

type Components = {
  Content: typeof Content;
  Footer: typeof Footer;
};

type ComponentProps = {
  className?: string;
};

type Props<T> = PropsWithChildren<T> & {
  isFullPage?: boolean;
  hasBreadcrumbs?: boolean;
  sx?: SxProps<Theme>;
};

const PageCardTemplate = ({
  children,
  hasBreadcrumbs = false,
  isFullPage,
  ...props
}: Props<{}>): ReactElement<Components> => {
  return (
    <StyledCard
      {...props}
      hasBreadcrumbs={hasBreadcrumbs}
      isFullPage={isFullPage}
    >
      {children}
    </StyledCard>
  );
};

const Content: React.FC<ComponentProps> = ({ children, className }) => {
  return (
    <CardContent className={cx('cardContent', className)}>
      {children}
    </CardContent>
  );
};

const Footer: React.FC<ComponentProps> = ({ children, className }) => {
  return (
    <CardActions className={cx('cardFooter', className)}>
      {children}
    </CardActions>
  );
};

PageCardTemplate.Content = Content;
PageCardTemplate.Footer = Footer;

export default PageCardTemplate;
